import React, { useEffect, useRef, useState } from "react";

import { ChartSpec } from "../../lib/dashboard-chart";
import { labelStride, showsLabel } from "../../lib/axis-labels";

interface ChartFigureProps {
  spec: ChartSpec;
}

const compact = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

/** Vertical pitch per category row (bar + gap). */
const ROW_HEIGHT = 36;
/** Drawn bar stroke; kept below ROW_HEIGHT so rows do not touch. */
const BAR_HEIGHT = 18;
const BAR_HEIGHT_MAX = 22;
const LABEL_WIDTH = 132;
const VALUE_WIDTH = 56;

/**
 * Shared by line and pie. Bars size from category count instead.
 */
const FIGURE_HEIGHT = 180;

/**
 * Kept for callers that imported the old width-derived floor. Bar height no
 * longer uses it: a 3-row Status chart was stretched to ~400px by card width.
 */
export const BAR_MIN_HEIGHT = ROW_HEIGHT;

/**
 * Height of the bar svg from the number of categories alone.
 *
 * Width used to raise the plot (`width * 0.36`, floor 280), which left three
 * Status bars as thin strips in a tall hole on the Sample Orders page. Height
 * now tracks row count with a fixed pitch and a capped bar stroke.
 */
export function barFigureHeight(
  pointCount: number,
  _measuredWidth?: number,
): number {
  return Math.max(pointCount, 1) * ROW_HEIGHT;
}

/**
 * Horizontal bars, one per category. Horizontal rather than vertical because
 * the categories are database values and routinely too long to sit under a
 * vertical axis without rotating the text.
 *
 * One series, so one hue and no legend: the title names what is plotted.
 */
/**
 * The figure is fluid (`width="100%"`), but bar geometry has to be numeric.
 *
 * SVG `x` / `width` on `<text>` are *geometry attributes*, and a geometry
 * attribute does not resolve a `calc()` containing a percentage: the browser
 * discards the whole expression and falls back to 0. That is how every bar's
 * value ended up painted at the far left of the figure instead of past its
 * bar, while the `<rect>` beside it looked right because `width` is also a CSS
 * property there and CSS does resolve the same calc. Measuring the rendered
 * width and doing the arithmetic in JS keeps the layout fluid without relying
 * on that inconsistency.
 */
const useMeasuredWidth = (): [React.RefObject<SVGSVGElement>, number] => {
  const ref = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const measure = () => setWidth(node.getBoundingClientRect().width);
    measure();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
};

/**
 * Bars are a share of the series total, not of the largest bucket.
 *
 * Scaling against `max` makes the biggest category fill the track by
 * definition, so `Projects by priority` painted High=5 almost to the right
 * edge of a 1088px card. Five of eleven rows reads as "nearly everything"
 * when the only reference is the bar beside it. Against the total, High=5
 * takes 45% of the track and the empty remainder is the other categories,
 * which is the comparison the card is actually for.
 *
 * A recessive full-width track sits behind each bar so the remainder is
 * visible rather than implied. Without it a 45% bar is just a shorter bar.
 */
const BarFigure: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const total = spec.points.reduce((sum, p) => sum + Math.max(p.value, 0), 0);
  // Fall back to the largest bucket when the total is unusable (all zero, or
  // a measure that summed to <= 0): a flat row of zero-length bars is worse
  // than the old relative read.
  const scale =
    total > 0 ? total : Math.max(...spec.points.map((p) => p.value), 0) || 1;
  const [ref, width] = useMeasuredWidth();
  const height = barFigureHeight(spec.points.length, width);
  const rowHeight = height / Math.max(spec.points.length, 1);
  // Pitch is fixed per row, so the stroke stays near BAR_HEIGHT rather than
  // growing with leftover svg. Cap so a one-category chart is not a slab.
  const barHeight = Math.min(
    BAR_HEIGHT_MAX,
    Math.max(BAR_HEIGHT, Math.round(rowHeight * 0.5)),
  );
  const plotWidth = Math.max(0, width - LABEL_WIDTH - VALUE_WIDTH);

  return (
    <svg
      ref={ref}
      width="100%"
      height={height}
      role="img"
      aria-label={spec.caption}
      style={{ overflow: "visible" }}
    >
      {spec.points.map((point, index) => {
        const y = index * rowHeight;
        const share = Math.max(point.value, 0) / scale;
        // A non-zero bucket always paints something: at 1 of 400 rows the
        // rounded rect would otherwise collapse to nothing and read as zero.
        const barWidth =
          point.value > 0
            ? Math.max(plotWidth * share, Math.min(plotWidth, 3))
            : 0;
        return (
          <g key={point.label}>
            <title>{`${point.label}: ${point.value.toLocaleString()}`}</title>
            <text
              x={LABEL_WIDTH - 8}
              y={y + rowHeight / 2}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-od-muted"
              style={{ fontSize: 12 }}
            >
              {point.label.length > 20
                ? `${point.label.slice(0, 19)}…`
                : point.label}
            </text>
            <rect
              x={LABEL_WIDTH}
              y={y + (rowHeight - barHeight) / 2}
              width={plotWidth}
              height={barHeight}
              rx={4}
              fill="var(--od-border)"
              opacity={0.35}
            />
            <rect
              x={LABEL_WIDTH}
              y={y + (rowHeight - barHeight) / 2}
              width={barWidth}
              height={barHeight}
              rx={4}
              fill="var(--od-primary)"
            />
            <text
              x={LABEL_WIDTH + plotWidth + 8}
              y={y + rowHeight / 2}
              dominantBaseline="middle"
              className="fill-od-text"
              style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
            >
              {compact(point.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const LINE_HEIGHT = FIGURE_HEIGHT;
const PAD_LEFT = 8;
const PAD_RIGHT = 16;
const PAD_BOTTOM = 22;

/**
 * Time series. 2px stroke, 8px markers, a recessive baseline, no grid.
 *
 * Path `d` is a list of user-unit numbers. Percentage tokens are legal on
 * `circle.cx` / `text.x` and illegal in `d`, so interpolating `14.28%` into
 * the path made the browser drop the whole stroke while the dots still
 * painted — which is how this read as a scatter. Same trap as the bar
 * labels: measure the svg and do the arithmetic in JS.
 */
const LineFigure: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const values = spec.points.map((p) => p.value);
  const max = Math.max(...values);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const plot = LINE_HEIGHT - PAD_BOTTOM;
  const [ref, width] = useMeasuredWidth();
  const inner = Math.max(0, width - PAD_LEFT - PAD_RIGHT);
  const step = inner / Math.max(spec.points.length - 1, 1);
  // Thin the month axis to what the measured width can hold. At 332px eight
  // `2026-01` labels overlapped into `2026-2026-02`; a wide card still labels
  // every bucket.
  const stride = labelStride(spec.points.length, inner);

  const at = (index: number, value: number) => ({
    x: PAD_LEFT + index * step,
    y: plot - ((value - min) / span) * (plot - 8) + 4,
  });

  const path = spec.points
    .map((point, index) => {
      const { x, y } = at(index, point.value);
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  return (
    <svg
      ref={ref}
      width="100%"
      height={LINE_HEIGHT}
      role="img"
      aria-label={spec.caption}
      style={{ overflow: "visible" }}
    >
      <line
        x1={PAD_LEFT}
        x2={Math.max(PAD_LEFT, width - PAD_RIGHT)}
        y1={plot + 4}
        y2={plot + 4}
        stroke="var(--od-border)"
        strokeWidth={1}
      />
      <path d={path} fill="none" stroke="var(--od-primary)" strokeWidth={2} />
      {spec.points.map((point, index) => {
        const { x, y } = at(index, point.value);
        const isEdge = index === 0 || index === spec.points.length - 1;
        return (
          <g key={point.label}>
            <title>{`${point.label}: ${point.value.toLocaleString()}`}</title>
            <circle
              cx={x}
              cy={y}
              r={4}
              fill="var(--od-primary)"
              stroke="var(--od-surface)"
              strokeWidth={2}
            />
            {/* Only the ends are labelled: a number on every point is noise. */}
            {isEdge && (
              <text
                x={x}
                y={y - 10}
                textAnchor={index === 0 ? "start" : "end"}
                className="fill-od-text"
                style={{ fontSize: 12, fontVariantNumeric: "tabular-nums" }}
              >
                {compact(point.value)}
              </text>
            )}
            {showsLabel(index, spec.points.length, stride) && (
              <text
                x={x}
                y={LINE_HEIGHT - 4}
                textAnchor={index === 0 ? "start" : isEdge ? "end" : "middle"}
                className="fill-od-muted"
                style={{ fontSize: 12 }}
              >
                {point.label}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

/**
 * Slice colours. One hue rotated through five steps rather than five unrelated
 * colours: the slices are values of a single dimension, so they should read as
 * one family, and a categorical rainbow would imply a meaning the data has not
 * got. Falls back around the ring for the rare six-plus-slice case.
 */
const SLICE_OPACITY = [1, 0.82, 0.64, 0.46, 0.3];

const polar = (cx: number, cy: number, r: number, turn: number) => ({
  // -0.25 starts the first slice at twelve o'clock, which is where a reader
  // expects a pie to begin.
  x: cx + r * Math.cos(2 * Math.PI * (turn - 0.25)),
  y: cy + r * Math.sin(2 * Math.PI * (turn - 0.25)),
});

/**
 * Part-to-whole for a classification column.
 *
 * A donut rather than a full pie: the hole carries the total, so the figure
 * answers "how many altogether" without a separate stat line, and thin slices
 * stay legible against the ring instead of collapsing into a point at the
 * centre.
 */
const PieFigure: React.FC<{ spec: ChartSpec }> = ({ spec }) => {
  const total = spec.points.reduce((sum, point) => sum + point.value, 0);
  const size = FIGURE_HEIGHT;
  const cx = size / 2;
  const cy = size / 2;
  const outer = size / 2 - 4;
  const inner = outer * 0.58;

  let cursor = 0;
  const slices = spec.points.map((point, index) => {
    const share = total > 0 ? point.value / total : 0;
    const from = cursor;
    cursor += share;
    return { point, share, from, to: cursor, index };
  });

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={spec.caption}
        style={{ flex: "0 0 auto" }}
      >
        {slices.map(({ point, share, from, to, index }) => {
          const opacity = SLICE_OPACITY[index % SLICE_OPACITY.length];
          // A lone category is a full ring; two arcs of a complete circle
          // cannot be drawn with a single elliptical arc command.
          if (share >= 1) {
            return (
              <g key={point.label}>
                <title>{`${point.label}: ${point.value.toLocaleString()}`}</title>
                <circle
                  cx={cx}
                  cy={cy}
                  r={(outer + inner) / 2}
                  fill="none"
                  stroke="var(--od-primary)"
                  strokeWidth={outer - inner}
                  opacity={opacity}
                />
              </g>
            );
          }
          const startOuter = polar(cx, cy, outer, from);
          const endOuter = polar(cx, cy, outer, to);
          const startInner = polar(cx, cy, inner, to);
          const endInner = polar(cx, cy, inner, from);
          const wide = share > 0.5 ? 1 : 0;
          const d = [
            `M ${startOuter.x} ${startOuter.y}`,
            `A ${outer} ${outer} 0 ${wide} 1 ${endOuter.x} ${endOuter.y}`,
            `L ${startInner.x} ${startInner.y}`,
            `A ${inner} ${inner} 0 ${wide} 0 ${endInner.x} ${endInner.y}`,
            "Z",
          ].join(" ");
          return (
            <g key={point.label}>
              <title>{`${point.label}: ${point.value.toLocaleString()}`}</title>
              <path d={d} fill="var(--od-primary)" opacity={opacity} />
            </g>
          );
        })}
        <text
          x={cx}
          y={cy - 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-od-text"
          style={{ fontSize: 18, fontWeight: 600 }}
        >
          {compact(total)}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-od-muted"
          style={{ fontSize: 12 }}
        >
          total
        </text>
      </svg>

      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {slices.map(({ point, share, index }) => (
          <li key={point.label} className="flex items-center gap-2 whitespace-nowrap text-xs">
            <span
              aria-hidden="true"
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: "var(--od-primary)",
                opacity: SLICE_OPACITY[index % SLICE_OPACITY.length],
                flex: "0 0 auto",
              }}
            />
            <span className="text-od-text">
              {point.label.length > 18
                ? `${point.label.slice(0, 17)}\u2026`
                : point.label}
            </span>
            <span
              className="text-od-muted"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {compact(point.value)} ({Math.round(share * 100)}%)
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ChartFigure: React.FC<ChartFigureProps> = ({ spec }) => {
  // Explicit per form. A fallthrough ternary is what let a stored PIE render
  // as bars, which hides the generator's choice instead of showing it.
  if (spec.form === "line") return <LineFigure spec={spec} />;
  if (spec.form === "pie") return <PieFigure spec={spec} />;
  return <BarFigure spec={spec} />;
};

export default ChartFigure;
